import {
  LINKEDIN_REGEX,
  NAME_REGEX,
  // PHONE_REGEX,
  DATE_REGEX,
  CERT_DATE_REGEX,
  TEXT_ONLY_REGEX,
  GRADE_REGEX,
  DURATION_REGEX,
  SALARY_REGEX,
  CREDENTIAL_ID_REGEX,
  DEGREE_REGEX,
  LANGUAGE_REGEX,
  DATE_REGEX_WITH_YEAR_ONLY,
  createNumberRegex,
  EXPERIENCE_REGEX,
} from "@/config/constants";
import { NOTICE_PERIOD_OPTIONS } from "@/config/dropdownData";
import { isStrictValidPhone } from "@/utils/phoneUtils";
import { fieldMatches } from "@/utils/reviewFormStatus";
import { validateAlternateEmail } from "@/utils/alternateEmail";
import {
  addDuplicateLanguageIssues,
  languageFieldRequirements,
  levelledLanguageKeys,
  REQUIRED_LANGUAGE_LEVEL_MESSAGE,
} from "@/utils/languageRequirements";
import { z } from "zod";

const currentYear = new Date().getFullYear();

const ONLY_NUMBER_REGEX = /^\d+$/;

/* ─────────────────────────────────────────────
   COMMON VALIDATORS
───────────────────────────────────────────── */

const optionalUrl = z
  .string()
  .trim()
  .max(500)
  .optional()
  .transform((v) => v?.trim() || undefined)
  .refine(
    (val) => {
      if (!val) return true;
      try {
        new URL(val.startsWith("http") ? val : `https://${val}`);
        return true;
      } catch {
        return false;
      }
    },
    { message: "Enter a valid URL" },
  );

// ─── Experience date validators ───────────────────────────────────────────────

const EXP_DATE_REGEX =
  /^(\d{4}-(0[1-9]|1[0-2]))$/;

const EXP_DATE_COMPAT =
  /^((0[1-9]|1[0-2])-\d{4}|(0[1-9]|1[0-2])\/\d{4})$/;

const isValidExpDate = (value) =>
  EXP_DATE_REGEX.test(value) ||
  EXP_DATE_COMPAT.test(value);

/**
 * Convert any supported employment date to a comparable month index.
 *
 * Supported:
 * YYYY-MM
 * MM-YYYY
 * MM/YYYY
 */
const expDateToMonthIndex = (value) => {
  if (!value) return null;

  const normalized = String(value)
    .trim()
    .toLowerCase();

  if (normalized === "present") {
    return null;
  }

  if (EXP_DATE_REGEX.test(normalized)) {
    const [year, month] =
      normalized
        .split("-")
        .map(Number);

    return year * 12 + (month - 1);
  }

  if (EXP_DATE_COMPAT.test(normalized)) {
    const [month, year] =
      normalized
        .split(/[-/]/)
        .map(Number);

    return year * 12 + (month - 1);
  }

  return null;
};

const currentMonthIndex =
  currentYear * 12 +
  new Date().getMonth();

const isFutureExpDate = (value) => {
  const monthIndex =
    expDateToMonthIndex(value);

  return (
    monthIndex !== null &&
    monthIndex > currentMonthIndex
  );
};

const optionalStartDate = z
  .string()
  .trim()
  .optional()
  .transform(
    (v) => v?.trim() || undefined,
  )
  .refine(
    (v) => !v || isValidExpDate(v),
    {
      message:
        "Use MM-YYYY (e.g., 09-2003)",
    },
  )
  .refine(
    (v) => !v || !isFutureExpDate(v),
    {
      message:
        "Start date cannot be in the future",
    },
  );

const requiredStartDate = z
  .string()
  .trim()
  .min(1, "Start Date is required")
  .transform(
    (v) => v?.trim() || undefined,
  )
  .refine(
    (v) => !v || isValidExpDate(v),
    {
      message:
        "Use MM-YYYY (e.g., 09-2003)",
    },
  )
  .refine(
    (v) => !v || !isFutureExpDate(v),
    {
      message:
        "Start date cannot be in the future",
    },
  );

const optionalEndDate = z
  .string()
  .trim()
  .toLowerCase()
  .optional()
  .transform(
    (v) => v?.trim() || undefined,
  )
  .refine(
    (v) =>
      !v ||
      v === "present" ||
      isValidExpDate(v),
    {
      message:
        "Use MM-YYYY or 'Present'",
    },
  )
  .refine(
    (v) =>
      !v ||
      v === "present" ||
      !isFutureExpDate(v),
    {
      message:
        "End date cannot be in the future",
    },
  );

const requiredEndDate = z
  .string()
  .trim()
  .toLowerCase()
  .min(1, "End Date is required")
  .transform(
    (v) => v?.trim() || undefined,
  )
  .refine(
    (v) =>
      !v ||
      v === "present" ||
      isValidExpDate(v),
    {
      message:
        "Use MM-YYYY or 'Present'",
    },
  )
  .refine(
    (v) =>
      !v ||
      v === "present" ||
      !isFutureExpDate(v),
    {
      message:
        "End date cannot be in the future",
    },
  );

// ─────────────────────────────────────────────────────────────────────────────

const optionalInstitution = z
  .string()
  .trim()
  .max(150)
  .optional()
  .transform((v) => v?.trim() || undefined)
  .refine((v) => !v || v.length <= 300, {
    message: "Institution name must be 300 characters or fewer",
  });

const requiredInstitution = z
  .string()
  .trim()
  .min(1, "Institution is required")
  .max(150)
  .transform((v) => v?.trim() || undefined)
  .refine((v) => !v || v.length <= 300, {
    message: "Institution name must be 300 characters or fewer",
  });

const optionalTextOnly = (msg = "Should contain letters only, no numbers") =>
  z
    .string()
    .trim()
    .optional()
    .refine((v) => !v || TEXT_ONLY_REGEX.test(v), { message: msg });

const optionalLocation = z
  .string()
  .trim()
  .max(150, "Location must be 150 characters or fewer")
  .optional()
  .transform((v) => v?.trim() || undefined)
  .refine((v) => !v || /^[A-Za-z0-9\s,.'()#/-]+$/.test(v), {
    message: "Enter a valid location",
  });

const requiredLocation = z
  .string()
  .trim()
  .min(1, "Location is required")
  .max(150, "Location must be 150 characters or fewer")
  .transform((v) => v?.trim() || undefined)
  .refine((v) => !v || /^[A-Za-z0-9\s,.'()#/-]+$/.test(v), {
    message: "Enter a valid location",
  });

/* ─────────────────────────────────────────────
   BUILD DYNAMIC PERSONAL SCHEMA
───────────────────────────────────────────── */
export const buildPersonalSchema = (mandatoryFields = []) => {
  // Required whatever the job config says. `reviewFormStatus` marks the same
  // four `always: true`, so the save-status chip counts what this refuses.
  const DEFAULT_REQUIRED = ["name", "email", "phone", "location"];
  const isFieldRequired = (fieldNames) => {
    if (DEFAULT_REQUIRED.some((f) => fieldNames.includes(f))) return true;
    return fieldMatches(mandatoryFields, fieldNames);
  };

  const isNameRequired = isFieldRequired(["personal_information.full_name", "personal_information.name", "name"]);
  const isEmailRequired = isFieldRequired(["personal_information.email", "email"]);
  const isPhoneRequired = isFieldRequired(["personal_information.phone", "phone"]);
  const isLocationRequired = isFieldRequired(["personal_information.location", "location"]);
  const isLinkedinRequired = isFieldRequired(["personal_information.linkedin", "linkedin"]);
  const isWebsiteRequired = isFieldRequired(["personal_information.website", "website"]);
  const isSummaryRequired = isFieldRequired(["summary", "personal_information.summary"]);

  return z
    .object({
      name: isNameRequired
        ? z
            .string({ required_error: "Name is required" })
            .trim()
            .min(1, "Name is required")
            .min(2, "Name must be at least 2 characters")
            .max(100, "Name must be 100 characters or fewer")
            .regex(NAME_REGEX, "Enter a valid name — letters only")
        : z.string().trim().max(100, "Name must be 100 characters or fewer").regex(NAME_REGEX, "Enter a valid name — letters only").optional(),

      email: isEmailRequired
        ? z.string().trim().min(1, "Email is required").email("Enter a valid email").max(250, "Email must be 250 characters or fewer")
        : z.string().trim().email("Enter a valid email").max(250, "Email must be 250 characters or fewer").optional(),
      phone: isPhoneRequired
        ? z
            .string({ required_error: "Phone number is required" })
            .trim()
            .min(1, "Phone number is required")
            .max(25, "Phone number is too long") // ← bump from 20 to 25
            .transform((v) => v?.replace(/\s+/g, " ").trim())
            .refine((v) => isStrictValidPhone(v), {
              message: "Enter a valid phone number",
            })
        : z
            .string()
            .trim()
            .max(25, "Phone number is too long") // ← bump from 20 to 25
            .transform((v) => v?.replace(/\s+/g, " ").trim())
            .refine((v) => !v || isStrictValidPhone(v), {
              message: "Enter a valid phone number",
            })
            .optional(),

      location: isLocationRequired ? requiredLocation : optionalLocation,

      linkedin: isLinkedinRequired
        ? z
            .string()
            .trim()
            .max(300)
            .min(1, "LinkedIn is required")
            .refine((v) => LINKEDIN_REGEX.test(v), { message: "Enter a valid LinkedIn URL" })
        : z
            .string()
            .trim()
            .max(300)
            .optional()
            .refine((v) => !v || LINKEDIN_REGEX.test(v), { message: "Enter a valid LinkedIn URL" }),

      portfolio: isWebsiteRequired
        ? z
            .string()
            .trim()
            .max(500)
            .min(1, "Website is required")
            .transform((v) => v?.trim() || undefined)
            .refine(
              (val) => {
                if (!val) return true;
                try {
                  new URL(val.startsWith("http") ? val : `https://${val}`);
                  return true;
                } catch {
                  return false;
                }
              },
              { message: "Enter a valid URL" },
            )
        : optionalUrl,

      summary: isSummaryRequired
        ? z.string().trim().min(1, "Summary is required").max(10000, "Summary must be 10000 characters or fewer")
        : z.string().trim().max(10000, "Summary must be 10000 characters or fewer").optional(),

      // Never required regardless of job config — the server never allows it to be.
      alternate_email: z.string().trim().optional(),
    })
    .superRefine((data, ctx) => {
      const message = validateAlternateEmail({ alternateEmail: data.alternate_email, primaryEmail: data.email });
      if (message) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["alternate_email"], message });
      }
    });
};

/* ─────────────────────────────────────────────
   BUILD DYNAMIC SKILLS SCHEMA
───────────────────────────────────────────── */
// `requiredLanguages` is the job's own required list. A row naming a language
// the job ASKED FOR A LEVEL IN must carry one: the row was prefilled and locked
// BECAUSE the job asks about that language at that level, so submitting it
// blank answers nothing.
//
// No other row is asked for a level — not a language the job named WITHOUT
// one, and not a language the candidate added. `languages.proficiency` goes
// mandatory for the whole job once any row is levelled, but it means "answer
// the levels this job asked for", and the server reads it the same way
// (`validate_mandatory_fields`). An unlevelled job language is still locked —
// the candidate cannot remove the question — which is why the editor's locks
// come from `requiredLanguageNames` and this rule from `levelledLanguageKeys`.
const languagePairRowsStrictSchema = (requireLang, requiredLanguages = []) => {
  const mustHaveLevel = levelledLanguageKeys(requiredLanguages);
  return z
    .array(
      z.object({
        language: z.string().trim().min(1, "Language is required"),
        proficiency: z.string().trim().optional().nullable(),
      })
    )
    .refine(
      (rows) => {
        if (requireLang && rows.length === 0) return false;
        return true;
      },
      { message: "At least one language is required for this job" }
    )
    .superRefine((rows, ctx) => {
      addDuplicateLanguageIssues(rows, ctx);
      rows.forEach((row, index) => {
        if (row.proficiency && row.proficiency.trim()) return;
        if (!mustHaveLevel.has(String(row.language || "").trim().toLowerCase())) return;
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [index, "proficiency"],
          message: REQUIRED_LANGUAGE_LEVEL_MESSAGE,
        });
      });
    });
};

export const buildSkillsSchema = (mandatoryFields = [], { languagesShape = "strings", requiredLanguages = [] } = {}) => {
  const isSkillsRequired = mandatoryFields.some((f) => f === "skills" || f === "skills.list");
  // Same derivation the Skills step marks its labels with, so a field can never
  // be refused here without carrying a marker there.
  const { language: isLanguagesRequired } = languageFieldRequirements(mandatoryFields);

  if (languagesShape === "rows") {
    return z.object({
      list: isSkillsRequired
        ? z
            .array(
              z
                .string()
                .trim()
                .min(1)
                .max(300, "Skill must be 300 characters or fewer")
                .refine((val) => /[a-zA-Z]/.test(val), "Skill must not contain only numbers or special characters"),
            )
            .min(1, "At least one skill is required")
        : z
            .array(
              z
                .string()
                .trim()
                .min(1)
                .max(300, "Skill must be 300 characters or fewer")
                .refine((val) => !val || /[a-zA-Z]/.test(val), "Skill must not contain only numbers or special characters"),
            )
            .optional(),
      languages: languagePairRowsStrictSchema(isLanguagesRequired, requiredLanguages),
    });
  }


  return z.object({
    list: isSkillsRequired
      ? z
        .array(
          z
            .string()
            .trim()
            .min(1)
            .max(300, "Skill must be 300 characters or fewer")
            .refine((val) => /[a-zA-Z]/.test(val), "Skill must not contain only numbers or special characters"),
        )
        .min(1, "At least one skill is required")
      : z
        .array(
          z
            .string()
            .trim()
            .min(1)
            .max(300, "Skill must be 300 characters or fewer")
            .refine((val) => !val || /[a-zA-Z]/.test(val), "Skill must not contain only numbers or special characters"),
        )
        .optional(),

    languages: isLanguagesRequired
      ? z
        .array(
          z
            .string()
            .min(2, "Language is required")
            .max(100)
            .refine((v) => LANGUAGE_REGEX.test(v), {
              message: "Enter a valid language (letters only, no numbers or symbols)",
            }),
        )
        .min(1, "At least one language is required")
        .optional()
      : z
        .array(
          z
            .string()
            .min(2)
            .max(100)
            .refine((v) => LANGUAGE_REGEX.test(v), {
              message: "Enter a valid language (letters only, no numbers or symbols)",
            }),
        )
        .optional(),
  });
};
/* ─────────────────────────────────────────────
   BUILD DYNAMIC EXPERIENCE SCHEMA
───────────────────────────────────────────── */

export const buildExperienceSchema = (
  mandatoryFields = [],
) => {
  const isJobTitleRequired =
    mandatoryFields.some(
      (f) =>
        f === "work_experience.title" ||
        f === "experience.jobTitle" ||
        f === "jobTitle",
    );

  const isCompanyRequired =
    mandatoryFields.some(
      (f) =>
        f === "work_experience.company" ||
        f === "experience.company" ||
        f === "company",
    );

  const isStartDateRequired =
    mandatoryFields.some(
      (f) =>
        f ===
        "work_experience.start_date" ||
        f === "experience.startDate" ||
        f === "startDate",
    );

  const isEndDateRequired =
    mandatoryFields.some(
      (f) =>
        f ===
        "work_experience.end_date" ||
        f === "experience.endDate" ||
        f === "endDate",
    );

  const isLocationRequired =
    mandatoryFields.some(
      (f) =>
        f ===
        "work_experience.location" ||
        f === "experience.location" ||
        f === "location",
    );

  const isDescriptionRequired =
    mandatoryFields.some(
      (f) =>
        f ===
        "work_experience.description" ||
        f ===
        "experience.description" ||
        f === "description",
    );

  return z.array(
    z
      .object({
        jobTitle: isJobTitleRequired
          ? z
            .string()
            .trim()
            .min(
              1,
              "Job Title is required",
            )
            .max(
              150,
              "Job title must be 150 characters or fewer",
            )
          : z
            .string()
            .trim()
            .optional(),

        company: isCompanyRequired
          ? z
            .string()
            .trim()
            .min(
              1,
              "Company is required",
            )
            .max(
              150,
              "Company name must be 150 characters or fewer",
            )
          : z
            .string()
            .trim()
            .optional(),

        startDate: isStartDateRequired
          ? requiredStartDate
          : optionalStartDate,

        endDate: isEndDateRequired
          ? requiredEndDate
          : optionalEndDate,

        location: isLocationRequired
          ? requiredLocation
          : optionalLocation,

        description:
          isDescriptionRequired
            ? z
              .string()
              .max(10000)
              .trim()
              .min(
                1,
                "Description is required",
              )
            : z
              .string()
              .max(10000)
              .optional(),
      })
      .superRefine((data, ctx) => {
        const {
          startDate,
          endDate,
        } = data;

        /*
         * Optional dates may both be empty, but once one side of the
         * employment range is supplied, the other side is required.
         */
        if (
          startDate &&
          !endDate
        ) {
          ctx.addIssue({
            code:
              z.ZodIssueCode.custom,
            path: ["endDate"],
            message:
              "End date is required when a start date is provided.",
          });

          return;
        }

        if (
          !startDate &&
          endDate
        ) {
          ctx.addIssue({
            code:
              z.ZodIssueCode.custom,
            path: ["startDate"],
            message:
              "Start date is required when an end date is provided.",
          });

          return;
        }

        if (
          !startDate ||
          !endDate ||
          endDate.toLowerCase() ===
          "present"
        ) {
          return;
        }

        const start =
          expDateToMonthIndex(
            startDate,
          );

        const end =
          expDateToMonthIndex(endDate);

        /*
         * Invalid formats are already handled by the individual field
         * validators, so don't create a second range error here.
         */
        if (
          start === null ||
          end === null
        ) {
          return;
        }

        /*
         * Same-month employment is valid.
         *
         * Example:
         * Start: 06-2026
         * End:   06-2026
         */
        if (end < start) {
          ctx.addIssue({
            code:
              z.ZodIssueCode.custom,
            path: ["endDate"],
            message:
              "End date cannot be earlier than the start date.",
          });
        }
      }),
  );
};

/* ─────────────────────────────────────────────
   BUILD DYNAMIC EDUCATION SCHEMA
───────────────────────────────────────────── */
export const buildEducationSchema = (mandatoryFields = []) => {
  const isInstitutionRequired = mandatoryFields.some((f) => f === "education.institution" || f === "institution");
  const isDegreeRequired = mandatoryFields.some((f) => f === "education.degree" || f === "degree");
  const isFieldOfStudyRequired = mandatoryFields.some((f) => f === "education.field_of_study" || f === "fieldOfStudy");
  const isStartDateRequired = mandatoryFields.some((f) => f === "education.start_date" || f === "startDate");
  const isEndDateRequired = mandatoryFields.some((f) => f === "education.end_date" || f === "endDate");
  const isGradeRequired = mandatoryFields.some((f) => f === "education.grade" || f === "grade");

  return z.array(
    z
      .object({
        institution: isInstitutionRequired ? requiredInstitution : optionalInstitution,

        degree: isDegreeRequired
          ? z
            .string()
            .trim()
            .min(1, "Degree is required")
            .max(200, "Degree must be 200 characters or fewer")
            .transform((v) => v?.trim())
            .refine((v) => !v || v.length <= 150, { message: "Degree must be 150 characters or fewer" })
          : z.string().trim().max(200, "Degree must be 200 characters or fewer").optional(),

        fieldOfStudy: isFieldOfStudyRequired
          ? z
            .string()
            .trim()
            .min(1, "Field of study is required")
            .max(150, "Field of study must be 150 characters or fewer")
            .refine((v) => !v || TEXT_ONLY_REGEX.test(v), { message: "Field of study should be text only" })
          : z
            .string()
            .trim()
            .max(150, "Field of study must be 150 characters or fewer")
            .optional()
            .refine((v) => !v || TEXT_ONLY_REGEX.test(v), { message: "Field of study should be text only" }),

        startDate: isStartDateRequired
          ? z
            .string()
            .trim()
            .min(1, "Start Year is required")
            .transform((v) => v?.trim() || undefined)
            .refine((v) => !v || createNumberRegex(4).test(v), { message: "Use YYYY (e.g., 2003)" })
            .refine((v) => !v || parseInt(v, 10) <= currentYear, { message: `Year must not be greater than the current year (${currentYear})` })
          : z
            .string()
            .trim()
            .transform((v) => v?.trim() || undefined)
            .refine((v) => !v || createNumberRegex(4).test(v), { message: "Use YYYY (e.g., 2003)" })
            .refine((v) => !v || parseInt(v, 10) <= currentYear, { message: `Year must not be greater than the current year (${currentYear})` })
            .optional(),

        endDate: isEndDateRequired
          ? z
            .string()
            .trim()
            .min(1, "End Year is required")
            .transform((v) => v?.trim() || undefined)
            .refine(
              (v) => !v || /^present$/i.test(v) || DATE_REGEX_WITH_YEAR_ONLY.test(v), // ← add /^present$/i check
              { message: "Use YYYY (e.g., 2003) or 'Present'" },
            )
            .refine((v) => !v || /^present$/i.test(v) || parseInt(v, 10) <= currentYear, { message: `Year must not be greater than the current year (${currentYear})` })
          : z
            .string()
            .trim()
            .transform((v) => v?.trim() || undefined)
            .refine(
              (v) => !v || /^present$/i.test(v) || DATE_REGEX_WITH_YEAR_ONLY.test(v), // ← same here
              { message: "Use YYYY (e.g., 2003) or 'Present'" },
            )
            .refine((v) => !v || /^present$/i.test(v) || parseInt(v, 10) <= currentYear, { message: `Year must not be greater than the current year (${currentYear})` })
            .optional(),

        grade: isGradeRequired
          ? z
            .string()
            .min(1, "Grade is required")
            .max(50, "Grade must be 50 characters or fewer")
            .refine((v) => !v || GRADE_REGEX.test(v), { message: "Enter a valid grade — e.g. 8.5/10, 3.5/4.0, 75%, A+" })
          : z
            .string()
            .max(50, "Grade must be 50 characters or fewer")
            .refine((v) => !v || GRADE_REGEX.test(v), { message: "Enter a valid grade — e.g. 8.5/10, 3.5/4.0, 75%, A+" })
            .optional(),
      })
      .superRefine((data, ctx) => {
        const { startDate, endDate } = data;
        if (!startDate || !endDate || endDate === "Present") return;
        const start = parseInt(startDate, 10);
        const end = parseInt(endDate, 10);
        if (start > end) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["endDate"], message: "End Year must be greater than or equal to Start Year" });
        }
      }),
  );
};

/* ─────────────────────────────────────────────
   BUILD DYNAMIC SALARY SCHEMA
───────────────────────────────────────────── */

// Salary lands in Candidate.current_salary / expected_salary, both
// Numeric(14, 2) — so twelve digits is the real ceiling. Anything wider, or any
// letter, is refused by Postgres at insert, and the recruiter only ever learns
// that as "skipped" on the confirm dialog. Keep in step with SALARY_MAX_DIGITS
// in server/app/api/v2/bulk_intake/service.py.
export const SALARY_MAX_DIGITS = 12;

// Optional means "you may leave it blank", never "anything goes". A recruiter
// who does not know a candidate's salary skips the field; one who types in it
// gets the same rules as a mandatory field.
const salaryField = (label, required) => {
  const withRules = (schema) =>
    schema
      .transform((v) => v?.trim() || undefined)
      .refine((v) => !v || ONLY_NUMBER_REGEX.test(v), { message: `${label} must contain numbers only` })
      .refine((v) => !v || v.length <= SALARY_MAX_DIGITS, { message: `${label} must be up to ${SALARY_MAX_DIGITS} digits only` });

  return required ? withRules(z.string().trim().min(1, `${label} is required`)) : withRules(z.string().trim().optional());
};

export const buildSalarySchema = (mandatoryFields = [], salaryFieldsRequired = true) => {
  const isFieldRequired = (fieldNames) => fieldMatches(mandatoryFields, fieldNames);

  const isCurrentSalaryRequired = salaryFieldsRequired && isFieldRequired(["current_salary", "compensation.current_salary"]);
  const isExpectedSalaryRequired = salaryFieldsRequired && isFieldRequired(["expected_salary", "compensation.expected_salary"]);
  const isNoticePeriodRequired = salaryFieldsRequired && isFieldRequired(["notice_period", "compensation.notice_period"]);
  const isTotalExperienceRequired = isFieldRequired(["total_experience", "compensation.total_experience"]);

  const noticePeriodValues = NOTICE_PERIOD_OPTIONS.map((item) => item.value);
  const optionalSalary = (numbersMessage, digitsMessage) =>
    z
      .string()
      .trim()
      .optional()
      .refine((v) => !v || ONLY_NUMBER_REGEX.test(v), { message: numbersMessage })
      .refine((v) => !v || validateSalaryDigits(v), { message: digitsMessage });

  return z.object({
    current_salary: salaryField("Gross salary", isCurrentSalaryRequired),

    expected_salary: salaryField("Expected gross salary", isExpectedSalaryRequired),

    notice_period: isNoticePeriodRequired
      ? z
        .string()
        .min(1, "Please select a notice period")
        .refine((value) => noticePeriodValues.includes(value), {
          message: "Please select a valid notice period",
        })
      : z.string().optional(),

    total_experience: isTotalExperienceRequired
      ? z
        .string()
        .min(1, "Total experience is required")
        .transform((v) => v?.trim() || undefined)
        .refine((v) => !v || EXPERIENCE_REGEX.test(v), {
          message: "Enter valid experience (e.g. 1, 5.9, 12, 12.11, max 100 years)",
        })
      : z
        .string()
        .optional()
        .transform((v) => v?.trim() || undefined)
        .refine((v) => !v || EXPERIENCE_REGEX.test(v), {
          message: "Enter valid experience (e.g. 1, 5.9, 12, 12.11, max 100 years)",
        }),
  });
};

/* ─────────────────────────────────────────────
   PUBLICATIONS & AWARDS
───────────────────────────────────────────── */
const publicationsSchema = z.array(z.string()).optional();
const awardsSchema = z.array(z.string()).optional();

/* ─────────────────────────────────────────────
   ADDITIONAL SECTIONS
───────────────────────────────────────────── */
const additionalSectionFieldSchema = z.object({
  key: z
    .string()
    .trim()
    .min(1, "Field is required")
    .max(300, "Field must be 300 characters or fewer")
    .transform((v) => v?.trim() || ""),
  value: z
    .string()
    .trim()
    .min(1, "Value is required")
    .max(10000, "Value must be 10000 characters or fewer")
    .transform((v) => v?.trim() || ""),
});

const additionalSectionsSchema = z.array(
  z.object({
    section_title: z.string().min(1, "Title required").max(100, "Title must be 100 characters or fewer"),
    items: z
      .array(z.array(additionalSectionFieldSchema))
      .refine((arr) => arr.length > 0 && arr.some((entry) => entry.some((f) => f.value !== "")), { message: "At least one item with a value is required" }),
  }),
);

/* ─────────────────────────────────────────────
   SOCIAL
───────────────────────────────────────────── */
const socialSchema = z.object({
  instagram_url: optionalUrl,
  facebook_url: optionalUrl,
  twitter_url: optionalUrl,
});

/* ─────────────────────────────────────────────
   BUILD COMPLETE DYNAMIC SCHEMA
───────────────────────────────────────────── */
export const buildReviewSchema = (mandatoryFields = [], salaryFieldsRequired = true, options = {}) => {
  return z.object({
    personal_information: buildPersonalSchema(mandatoryFields),
    skills: buildSkillsSchema(mandatoryFields, options),
    experience: buildExperienceSchema(mandatoryFields),
    education: buildEducationSchema(mandatoryFields),
    additionalSections: additionalSectionsSchema,
    social: socialSchema,
    compensation: buildSalarySchema(mandatoryFields, salaryFieldsRequired),
  });
};

/* ─────────────────────────────────────────────
   BUILD STEP-WISE DYNAMIC SCHEMAS
───────────────────────────────────────────── */
export const buildStepSchemas = (mandatoryFields = [], salaryFieldsRequired = true, options = {}) => [
  z.object({ personal_information: buildPersonalSchema(mandatoryFields) }),
  z.object({ skills: buildSkillsSchema(mandatoryFields, options) }),
  z.object({ experience: buildExperienceSchema(mandatoryFields) }),
  z.object({ education: buildEducationSchema(mandatoryFields) }),
  z.object({ compensation: buildSalarySchema(mandatoryFields, salaryFieldsRequired) }),
  z.object({ additionalSections: additionalSectionsSchema, social: socialSchema }),
];

// Default (non-mandatory) schemas for when no job_id is provided
export const reviewSchema = z.object({
  personal_information: buildPersonalSchema([]),
  skills: buildSkillsSchema([]),
  experience: buildExperienceSchema([]),
  education: buildEducationSchema([]),
  additionalSections: additionalSectionsSchema,
  social: socialSchema,
  compensation: buildSalarySchema([]),
});

export const stepSchemas = [
  z.object({ personal_information: buildPersonalSchema([]) }),
  z.object({ skills: buildSkillsSchema([]) }),
  z.object({ experience: buildExperienceSchema([]) }),
  z.object({ education: buildEducationSchema([]) }),
  z.object({ compensation: buildSalarySchema([]) }),
  z.object({ additionalSections: additionalSectionsSchema, social: socialSchema }),
];
